import { useState, type ChangeEvent } from 'react';
import type { RankedDay } from '../util/top-days.js';
import { formatGallons } from '../util/format.js';
import styles from './Top5Widget.module.css';

interface Top5WidgetProps {
  days: RankedDay[];
  // Current people-count values, keyed by yyyymmdd. Empty/missing = unknown.
  peopleCounts: Record<string, number>;
  // Set of dates currently being saved (for the inline indicator).
  savingDates: ReadonlySet<string>;
  onSelectDate: (yyyymmdd: string) => void;
  onChangePeopleCount: (yyyymmdd: string, peopleCount: number | undefined) => void;
  onDownload: () => void;
  downloading: boolean;
}

function formatLabel(yyyymmdd: string): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(`${yyyymmdd}T00:00:00`));
}

function formatGallonsPerPerson(gallons: number, count: number): string {
  return `${(gallons / count).toLocaleString('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} gal/person`;
}

interface RowProps {
  rank: number;
  day: RankedDay;
  count: number | undefined;
  saving: boolean;
  onSelectDate: (yyyymmdd: string) => void;
  onChangePeopleCount: (yyyymmdd: string, peopleCount: number | undefined) => void;
}

function TopDayRow({ rank, day, count, saving, onSelectDate, onChangePeopleCount }: RowProps) {
  const [inputValue, setInputValue] = useState<string>(count === undefined ? '' : String(count));

  // If the canonical count changes from the parent (e.g. after fetch), sync
  // the input UNLESS the user is mid-edit. We treat "input differs from
  // canonical" as "user is editing".
  if (
    count !== undefined &&
    inputValue === '' &&
    document.activeElement !== document.querySelector(`input[data-date="${day.yyyymmdd}"]`)
  ) {
    setInputValue(String(count));
  }

  const onInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const raw = e.target.value;
    setInputValue(raw);
    if (raw === '') {
      onChangePeopleCount(day.yyyymmdd, undefined);
      return;
    }
    const n = Number(raw);
    if (Number.isFinite(n) && Number.isInteger(n) && n >= 0 && n <= 1000) {
      onChangePeopleCount(day.yyyymmdd, n);
    }
  };

  return (
    <div className={styles.row}>
      <span className={styles.rank}>{rank}</span>
      <button
        type="button"
        className={styles.dateButton}
        onClick={() => onSelectDate(day.yyyymmdd)}
      >
        {formatLabel(day.yyyymmdd)}
      </button>
      <span className={styles.value}>{formatGallons(day.intervalGallons)}</span>
      <label className={styles.peopleLabel}>
        <span className={styles.peopleLabelText}>people</span>
        <input
          type="number"
          inputMode="numeric"
          min={0}
          max={1000}
          step={1}
          value={inputValue}
          onChange={onInputChange}
          className={styles.peopleInput}
          data-date={day.yyyymmdd}
          aria-label={`People count for ${formatLabel(day.yyyymmdd)}`}
        />
      </label>
      <span className={styles.perPerson}>
        {count !== undefined && count > 0
          ? formatGallonsPerPerson(day.intervalGallons, count)
          : ''}
      </span>
      <span className={styles.savingBadge} aria-live="polite">
        {saving ? 'Saving…' : ''}
      </span>
    </div>
  );
}

export function Top5Widget({
  days,
  peopleCounts,
  savingDates,
  onSelectDate,
  onChangePeopleCount,
  onDownload,
  downloading,
}: Top5WidgetProps) {
  if (days.length === 0) {
    return null;
  }

  return (
    <section className={styles.panel}>
      <header className={styles.header}>
        <h2 className={styles.title}>Top 5 highest-usage days</h2>
        <button
          type="button"
          className={styles.download}
          onClick={onDownload}
          disabled={downloading}
        >
          {downloading ? 'Downloading…' : 'Download Data'}
        </button>
      </header>
      <p className={styles.hint}>
        Click a day to see its hourly breakdown. Enter a people count to see gallons per person.
      </p>
      <div className={styles.list}>
        {days.map((d, i) => (
          <TopDayRow
            key={d.yyyymmdd}
            rank={i + 1}
            day={d}
            count={peopleCounts[d.yyyymmdd]}
            saving={savingDates.has(d.yyyymmdd)}
            onSelectDate={onSelectDate}
            onChangePeopleCount={onChangePeopleCount}
          />
        ))}
      </div>
    </section>
  );
}
